"use client";

import * as React from "react";
import { StaticImageData } from "next/image";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import LinkWithLang, {
  type LinkRouteDefaults,
} from "@stadium/shared/src/components/link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { B_09, BANNER_DEFAULTS } from "@stadium/shared/src/helpers/plasmic/static-slices";
import { type LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface B09Props {
  // Content
  heading?: string;
  description?: string;

  // CTAs
  primaryButtonText?: string;
  primaryButtonLinkType?: string;
  primaryButtonOpenInNewTab?: boolean;
  primaryButtonOtherUrl?: string;
  primaryButtonCategorySlug?: string;
  secondaryButtonText?: string;
  secondaryButtonLinkType?: string;
  secondaryButtonOpenInNewTab?: boolean;
  secondaryButtonOtherUrl?: string;
  secondaryButtonCategorySlug?: string;

  mobileImg?: string | StaticImageData;
  desktopImg?: string | StaticImageData;
  imageAlt?: string;

  // Layout & Styling
  backgroundColor?: string;
  className?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

const B09: React.FC<Readonly<B09Props>> = ({
  heading = BANNER_DEFAULTS[B_09].heading,
  description = BANNER_DEFAULTS[B_09].description,
  primaryButtonText = BANNER_DEFAULTS[B_09].primaryButtonText,
  primaryButtonLinkType = BANNER_DEFAULTS[B_09].primaryButtonLinkType,
  primaryButtonOpenInNewTab = false,
  primaryButtonOtherUrl,
  primaryButtonCategorySlug,
  secondaryButtonText = BANNER_DEFAULTS[B_09].secondaryButtonText,
  secondaryButtonLinkType = BANNER_DEFAULTS[B_09].secondaryButtonLinkType,
  secondaryButtonOpenInNewTab = false,
  secondaryButtonOtherUrl,
  secondaryButtonCategorySlug,
  imageAlt = BANNER_DEFAULTS[B_09].imageAlt,
  mobileImg = BANNER_DEFAULTS[B_09].mobileImg,
  desktopImg = BANNER_DEFAULTS[B_09].desktopImg,
  backgroundColor = BANNER_DEFAULTS[B_09].backgroundColor,
  className = "",
  linkRouteDefaults,
}) => {
  const { isMobileDevice, isTabletDevice } = React.useContext(CurrentDeviceContext);

  return (
    <section className={`${styles.sectionWrapper} ${className}`}>
      <div className={styles.divWrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.leftContentWrapper} style={{ backgroundColor }}>
            <div className={styles.leftContent}>
              <h2 className={styles.heading}>{heading}</h2>
              {description && <p className={styles.description}>{description}</p>}
              <div className={styles.buttonWrapper}>
                {primaryButtonText && primaryButtonLinkType && (
                  <LinkWithLang
                    {...linkRouteDefaults}
                    type={primaryButtonLinkType as LinkWithLangType}
                    data={{
                      code: primaryButtonCategorySlug,
                      url: primaryButtonOtherUrl,
                    }}
                    target={primaryButtonOpenInNewTab ? "_blank" : "_self"}
                    showExternalLinkIcon={primaryButtonOpenInNewTab}
                    className={styles.primaryBtn}
                  >
                    {primaryButtonText}
                  </LinkWithLang>
                )}
                {secondaryButtonText && secondaryButtonLinkType && (
                  <LinkWithLang
                    {...linkRouteDefaults}
                    type={secondaryButtonLinkType as LinkWithLangType}
                    data={{
                      code: secondaryButtonCategorySlug,
                      url: secondaryButtonOtherUrl,
                    }}
                    target={secondaryButtonOpenInNewTab ? "_blank" : "_self"}
                    showExternalLinkIcon={secondaryButtonOpenInNewTab}
                    className={styles.primaryOutlineBtn}
                  >
                    {secondaryButtonText}
                  </LinkWithLang>
                )}
              </div>
            </div>
          </div>

          <div className={styles.rightContent}>
            {isMobileDevice || isTabletDevice ? (
              <Multimedia type="image" src={mobileImg} alt={imageAlt} className={styles.image} />
            ) : (
              <Multimedia type="image" src={desktopImg} alt={imageAlt} className={styles.image} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
export default B09;
